import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  schema,
}: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = title
      ? `${title} | ${siteInfo.name}`
      : `${siteInfo.name} | ${siteInfo.tagline}`;

    document.title = pageTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, true);
    }
    if (keywords) setMeta('keywords', keywords);

    const url = canonicalUrl || `${siteInfo.url}${location.pathname}`;
    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) {
      linkEl.setAttribute('href', url);
    }

    setMeta('og:title', pageTitle, true);
    setMeta('og:url', url, true);
    setMeta('og:type', ogType, true);
    if (description) setMeta('twitter:description', description);

    if (schema) {
      const existingSchema = document.querySelector('script[type="application/ld+json"][data-seo="dynamic"]');
      if (existingSchema) existingSchema.remove();

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'dynamic');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const dynSchema = document.querySelector('script[type="application/ld+json"][data-seo="dynamic"]');
      if (dynSchema) dynSchema.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogType, schema, location.pathname]);

  return null;
}